import * as React from 'react';

import {getRecipientFromSelectedTextable} from 'shells/inbox/helpers';
import {ChannelViewLayout} from 'shells/inbox/messages/channel-view-layout';
import type {Textable} from 'shells/inbox/types';

import {ThreadType} from 'nutshell-graphql-types';
import type {
    GetInboxThread_inboxThread as InboxThread,
    InboxChannelFragment as Channel,
} from 'nutshell-graphql-types';

import {useGetSessionUser} from '../../../core/use-get-session-user';
import {useGetInboxStatus} from '../../../setup/text-messaging/graphql/hooks/use-get-inbox-status';

import {useGetInboxMessage} from '../../graphql/hooks';
import {getCantSendStatusEnum} from '../../helpers';
import {
    useDeleteTextMessage,
    useResendTextMessage,
    useSendTextMessage,
} from '../../sms/graphql/hooks';

type Props = {
    isLoadingTextables: boolean;
    isAutoSelectingTextable: boolean;
    selectedTextable: Textable | null | undefined;
    selectedChannel: Channel | null | undefined;
    setSelectedChannel: (channel?: Channel | null | undefined) => void;
    textables: Textable[];
    includeLeadIds: boolean;
    entityId: string;
    openedFromEntityName: string | null | undefined;
    onCloseSidebar: () => void;
    selectedThread: InboxThread | null | undefined;
    openThreadIdOnSelectedChannel: string | null | undefined;
    setSelectedThreadId: (threadId?: string | null | undefined) => void;
    isStartingNewThread: boolean;
    setIsStartingNewThread: (arg1: boolean) => void;
    refetchTextables: () => Promise<any>;
    setSelectedTextableDidUpdate: (arg1: boolean) => void;
    isLoadingChannelEvents: boolean;
    channelEvents: any[];
    fetchMoreRows: () => any;
    hasNextPage: boolean;
    fetchMostRecentChannelEvents: () => Promise<any>;
    refetchScheduledChannelEvents: () => Promise<any>;
};

export function SidebarContentWrapper(props: Props) {
    const {isStartingNewThread, selectedThread} = props;
    const [newMessageId, setNewMessageId] = React.useState<any>(null);
    const [newMessageThreadId, setNewMessageThreadId] = React.useState<any>(null);
    const [sendIsComplete, setSendIsComplete] = React.useState(false);
    const [sendIsFailed, setSendIsFailed] = React.useState(false);
    // Starts as undefined, will be set to a status or null once the function runs
    const [cantSendStatus, setCantSendStatus] = React.useState<any>(undefined);
    const selectedThreadId = selectedThread && selectedThread.id;
    const {inboxStatus} = useGetInboxStatus();
    const {sendTextMessage} = useSendTextMessage();
    const {inboxMessage} = useGetInboxMessage(newMessageId);
    const {user} = useGetSessionUser();

    // once we get the events for the newly created thread, clear starting new state
    React.useEffect(() => {
        if (selectedThreadId === newMessageThreadId) {
            props.setIsStartingNewThread(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newMessageThreadId, selectedThreadId]);

    React.useEffect(() => {
        if (inboxMessage) {
            if (inboxMessage.status === 'SENT') {
                setSendIsComplete(true);
                // clear complete state after a .5 seconds
                setTimeout(() => setSendIsComplete(false), 500);
            } else if (inboxMessage.status === 'FAILED') {
                setSendIsFailed(true);
            }
        }
    }, [inboxMessage]);

    const handleSend = (input: {content: string; snoozeDuration?: number}) => {
        const externalNumber =
            props.selectedTextable &&
            props.selectedTextable.phone &&
            props.selectedTextable.phone.value
                ? props.selectedTextable.phone.value.id
                : null;

        if (!externalNumber) {
            return Promise.resolve();
        }

        const threadId =
            !props.isStartingNewThread && selectedThread && selectedThread.isOpen
                ? selectedThread.id
                : undefined;

        return sendTextMessage({
            externalNumber,
            threadId,
            leadIds: props.includeLeadIds ? [props.entityId] : undefined,
            ...input,
        }).then((response) => {
            const message =
                response &&
                response.data &&
                response.data.inboxTextMessageSend &&
                response.data.inboxTextMessageSend.message;

            const messageId = message && message.id;
            const messageThreadId = message && message.thread && message.thread.id;
            const messageChannel = message && message.thread && message.thread.externalChannel;

            setNewMessageId(messageId);
            props.setSelectedThreadId(messageThreadId);
            setNewMessageThreadId(messageThreadId);

            if ((!props.selectedChannel || props.isStartingNewThread) && messageChannel) {
                props.refetchTextables().then(() => {
                    props.setSelectedTextableDidUpdate(true);
                    props.setSelectedChannel(messageChannel);
                });
            }
        });
    };

    const {resendTextMessage} = useResendTextMessage();
    const onResendMessage = (messageId: string) => {
        return resendTextMessage({
            messageId,
            channelId: props.selectedChannel && props.selectedChannel.id,
            threadId: selectedThread && selectedThread.id,
        }).then((response) => {
            const message =
                response &&
                response.data &&
                response.data.textResend &&
                response.data.textResend.message;

            const resentMessageId = message && message.id;
            setNewMessageId(resentMessageId);
        });
    };

    const {deleteTextMessage} = useDeleteTextMessage();
    const onDeleteMessage = (messageId: string) => {
        return deleteTextMessage({
            messageId,
            channelId: props.selectedChannel && props.selectedChannel.id,
            threadId: selectedThread && selectedThread.id,
        });
    };

    const noValidRecipients = !props.isLoadingTextables && props.textables.length === 0;
    const recipient = props.selectedTextable
        ? getRecipientFromSelectedTextable(props.selectedTextable)
        : undefined;

    const channelStatus = props.selectedChannel && props.selectedChannel.status;

    React.useEffect(() => {
        if (user && inboxStatus) {
            const statusEnum = getCantSendStatusEnum({
                isInboxPage: false,
                userCanUseSms: user.permissions.canUseSms,
                inboxStatus,
                channelStatus,
                selectedThread: isStartingNewThread ? undefined : selectedThread,
                hasSmsCampaigns:
                    NutClientConfig.hasSmsCampaignsFeature && user.permissions.canAccessMarketing,
            });
            setCantSendStatus(statusEnum);
        }
    }, [user, inboxStatus, channelStatus, selectedThread, isStartingNewThread]);

    return (
        <ChannelViewLayout
            onSend={handleSend}
            isLoadingTextables={props.isLoadingTextables}
            isAutoSelectingTextable={props.isAutoSelectingTextable}
            isLoadingChannelEvents={props.isLoadingChannelEvents}
            recipient={recipient}
            noValidRecipients={noValidRecipients}
            channelEvents={props.channelEvents}
            fetchMoreRows={props.fetchMoreRows}
            hasNextPage={props.hasNextPage}
            isSendComplete={sendIsComplete}
            isSendFailed={sendIsFailed}
            onResendMessage={onResendMessage}
            onDeleteMessage={onDeleteMessage}
            openedFromEntityName={props.openedFromEntityName}
            cantSendStatus={cantSendStatus}
            onCloseSidebar={props.onCloseSidebar}
            fetchMostRecentChannelEvents={props.fetchMostRecentChannelEvents}
            refetchScheduledChannelEvents={props.refetchScheduledChannelEvents}
            isStartingNewThread={props.isStartingNewThread}
            setIsStartingNewThread={props.setIsStartingNewThread}
            setViewOpenThread={
                props.openThreadIdOnSelectedChannel
                    ? () => props.setSelectedThreadId(props.openThreadIdOnSelectedChannel)
                    : undefined
            }
            canStartNewThreads={Boolean(user && user.permissions.canUseSms)}
            threadType={ThreadType.SMS}
            threadId={selectedThreadId || undefined}
            isSidebar={true}
        />
    );
}
